<template>
  <div class="site-search">
    <div
      class="sticky top-0 z-[200] bg-back-bg pt-[40px] -mx-[60px] px-[60px] pb-[20px]"
    >
      <div>
        <Search
          ref="searchRef"
          v-model="keyword"
          size="large"
          class="w-[800px] mx-auto"
          :placeholder="$t('sou-suo')"
          :showHot="true"
          :area="SEARCH_RECORD_UNIQUE.SITE"
          @search="handleOnSearch"
        >
        </Search>
      </div>
      <div class="mt-[12px] flex-y-center justify-center">
        <template v-for="item in newTabKeys" :key="item.key">
          <span
            v-if="
              item.key === 'all'
                ? getTotalCount()
                : totalList?.[item.key]?.total
            "
            class="tabs"
            :class="{
              'tabs-active': activeKey === item.key,
            }"
            @click="chooseKey(item.key)"
          >
            {{ item.label }}
            {{
              item.key === 'all'
                ? getTotalCount()
                : totalList?.[item.key]?.total
            }}
          </span>
        </template>
      </div>
    </div>
    <div class="pb-[30px]">
      <template v-for="x in newTabKeys" :key="x.key">
        <component
          :is="SideSearchPage['' + x.key]"
          v-if="
            x.key === activeKey &&
            (x.key === 'all' ? list?.length && !pending : list.length)
          "
          :menu="newTabKeys"
          :data="x.key === 'all' ? totalList : list"
          :words="splitData?.data"
          @select="seeMore"
        />
      </template>
      <div v-if="activeKey !== 'all'">
        <div ref="wp" v-intersect="handleInView" class="way-point"></div>
        <Divider v-if="list.length === total && total > 20" />
      </div>
    </div>
    <div
      v-if="pending"
      class="w-full flex-center mt-[20px] bottom-0 left-0 z-8 pending"
    >
      <a-spin />
    </div>
    <Empty
      v-if="
        !pending && (activeKey !== 'all' ? !list?.length : !getTotalCount())
      "
      :list="popularList?.data"
      @select="handleOnSearch"
      @refresh="popularRefresh"
    />
  </div>
</template>

<script setup lang="ts">
import Page from './components/page'
import Empty from '@/features/searchInput/empty.vue'
import Search from '@/features/searchInput/index.vue'
import { SEARCH_HOT_WORD, SEARCH_RECORD_UNIQUE } from '~/constants'
import {
  getSiteHotWord,
  getSiteSearchResult,
  getSiteSegmentWord,
} from '~/api/module/search'
import { SCROLL_LIST, useScrollList } from '~/hooks/useScrollList'
import { PageKey } from '~/constants/page'

const SideSearchPage = Page as Record<string, any>
const route: any = useRoute()
const keyword = ref<string>(route.query.keyword || '')
const searchRef = ref()
const router = useRouter()
const { $t } = useNuxtApp()

const activeKey = ref(route.query.active || 'all')
const tabKeys: any = useDicts(isChinese() ? 'site' : 'foreignSite')

provideResourceLocation({
  source: SOURCE.SITE_SEARCH,
})

const handleOnSearch = (val: string) => {
  if (!val) {
    router.push({
      name: PageKey.HOME,
    })
    return
  }
  keyword.value = val
  activeKey.value = 'all'
  refreshSplit()
  refreshTotal()
}

const { data: popularList, refresh: popularRefresh } = useAsyncData(() =>
  getSiteHotWord(SEARCH_HOT_WORD.SITE, 6)
)

watch([() => activeKey.value, () => keyword.value], ([_key, _keyword]) => {
  if (route.query.active !== _key || route.query.keyword !== _keyword) {
    // 路由和内容不匹配，则修改
    router.replace({
      query: {
        keyword: _keyword,
        active: _key,
      },
    })
  }
})

const { list, pending, refresh, next, total } = useScrollList(
  async (params) => {
    const p = {
      ...params,
      page_size: activeKey.value !== 'all' ? 20 : 10,
      q: keyword.value || undefined,
      category: activeKey.value !== 'all' ? activeKey.value : undefined,
    }
    const res: any = await getSiteSearchResult(p)

    return {
      ...res,
      data: {
        results:
          activeKey.value !== 'all'
            ? rehandleList(res.data).data || []
            : [rehandleList(res.data, true)],
        count: activeKey.value !== 'all' ? rehandleList(res.data)?.total : 0,
      },
    }
  },
  {
    key: SCROLL_LIST.SITE_SEARCH,
  }
)

const { data: allList, refresh: refreshTotal } = useAsyncData(() =>
  getSiteSearchResult({
    page_size: 10,
    page: 1,
    q: keyword.value || undefined,
  })
)

// const totalList = computed(() => allList?.value?.data || [])
// 重新处理下数据格式
const totalList: any = computed(() => rehandleList(allList?.value?.data, true))

const rehandleList = (data: any, all?: boolean) => {
  if (!all) {
    return data.find((item: any) => {
      const key = Object.keys(item)[0]

      return key === activeKey.value
    })[activeKey.value]
  } else {
    let obj = {}
    data?.forEach((element: any) => {
      obj = {
        ...obj,
        ...element,
      }
    })

    return obj
  }
}

// 重新处理下默认tab顺序
const newTabKeys: any = computed(() => {
  const newArray: any = [].concat(tabKeys?.[0])

  Object.keys(totalList.value).forEach((key) => {
    const data: any = tabKeys.find((item: any) => item.key === key)
    if (data) {
      newArray.push(data)
    }
  })
  return newArray
})

const { data: splitData, refresh: refreshSplit } = useAsyncData(() => {
  if (!keyword.value) {
    return Promise.resolve({
      code: 0,
      data: [],
    })
  }
  return getSiteSegmentWord(keyword.value)
})

const seeMore = (val: string) => {
  activeKey.value = val
  refresh()
}

const handleInView = (inView: boolean) => {
  inView && next()
}

const chooseKey = (key: string) => {
  activeKey.value = key
  refresh()
}

const getTotalCount = () => {
  const data: any = totalList.value
  let totalCount = 0

  for (const key in data) {
    const item = data[key]
    if (item && item.total !== undefined) {
      totalCount += item.total
    }
  }

  return totalCount
}
</script>

<style lang="scss" scoped>
.site-search {
  @apply w-[1200px] mx-auto;
  // height: calc(100vh - 60px);
  // overflow-y: scroll;

  .tabs {
    @apply text-12 text-font-80 p-[7px_12px] cursor-pointer;
  }
  .tabs-active {
    @apply bg-back-3 rounded-[8px] text-font-40;
  }
}
</style>
